import React, { useCallback } from 'react';
import {
  IncidentFiltersSourceProvider,
  IncidentsFeedSourceProvider,
  PanelHostProvider,
} from '@/core/modules/MapPanelSources';
import type { IncidentFeedItem } from '@/server/services/GoogleBigQuery/BigQueryIncidentsClient.types';
import { CLUSTER_MAX_ZOOM } from '../MapView/MapDataDrawer/MapDataDrawer';
import { useFiltersContext } from './FiltersContextProvider';
import { useMapContext } from './MapContextProvider';
import { useMapEventsContext } from './MapEventsContextProvider';

/**
 * SA adapters over the SA contexts for the neutral panel sources; the panels read only these.
 * Each provider now takes typed props and builds its own value, so this host only wires SA state in.
 */
export const PanelSourcesProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { map, moveMapAside } = useMapContext();
  const { filters, filterFlags, applyFilters, resetFilters } = useFiltersContext();
  const { incidentsData, activeIncidents, setActiveIncidents, isGovernmentLevels, setActiveTarget } =
    useMapEventsContext();

  const setMapAside = useCallback(
    (aside: boolean) => {
      if (!map) return;
      moveMapAside(aside);
      map.resize();
    },
    [map, moveMapAside],
  );

  const showIncidentDetails = useCallback((item: IncidentFeedItem) => setActiveTarget(item), [setActiveTarget]);

  const focusMaxZoom = isGovernmentLevels ? CLUSTER_MAX_ZOOM * 0.7 : undefined;

  return (
    <PanelHostProvider onMapAsideChange={setMapAside}>
      <IncidentFiltersSourceProvider
        filters={filters}
        filterFlags={filterFlags}
        onApply={applyFilters}
        onReset={resetFilters}
      >
        <IncidentsFeedSourceProvider
          map={map}
          incidents={incidentsData}
          activeIncidents={activeIncidents}
          onActiveIncidentsChange={setActiveIncidents}
          onShowIncidentDetails={showIncidentDetails}
          focusMaxZoom={focusMaxZoom}
        >
          {children}
        </IncidentsFeedSourceProvider>
      </IncidentFiltersSourceProvider>
    </PanelHostProvider>
  );
};
